import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Moment } from "moment";
import styled from "styled-components";
import { closeDialog } from '@shared/store/dialog.duck';
import Dialog from "@shared/ui/molecules/Dialog";
import DialogHeader from '@shared/dialogs/DialogHeader';
import TextField from '@shared/ui/atoms/TextField';
import { DateInputField } from '@shared/ui/molecules/DateInputField';
import FullScreenDateRangeSelector from '@shared/ui/molecules/calendar/FullScreenDateRangeSelector';
import TeamDropDownList from '@shared/ui/organisms/team-drop-down-list/TeamDropDownList';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { BodyError } from '@shared/ui/atoms/fonts/Body';
import { smallTablet } from '@shared/ui/atoms/MediaQueries';
import { selectAllTeams } from '@shared/store/config.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { canUserCreateTeamEvent } from '@shared/utils/AccessControl';
import { createTeamEventRequest } from '@features/wall-planner/WallPlannerService';
import { loadTeamEvents } from '@features/wall-planner/pages/wallPlanner.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';

export function AddProjectOrEventDialog() {
  const dispatch = useDispatch();
  const teams = useSelector(selectAllTeams);
  const currentUser = useSelector(selectCurrentUser);

  const [isLoading, setIsLoading] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [eventName, setEventName] = useState('');
  const [fromDate, setFromDate] = useState<Moment | null>(null);
  const [toDate, setToDate] = useState<Moment | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const [fromDateError, setFromDateError] = useState('');
  const [toDateError, setToDateError] = useState('');

  useEffect(() => {
    if (currentUser?.teamEntity?.id) {
      setSelectedTeamId(currentUser.teamEntity.id);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !teams || !selectedTeamId) {
      return;
    }
    if (!canUserCreateTeamEvent(currentUser, teams.find(t => t.id === selectedTeamId))) {
      setError('You cannot add an event to that team. Please ask an Admin or a team member of that team.');
    } else {
      setError('');
    }
  }, [selectedTeamId, currentUser, teams]);

  const onDateSelected = (startDate?: Moment, endDate?: Moment) => {
    if (startDate) setFromDate(startDate);
    if (endDate) setToDate(endDate);
  }

  const validate = () => {
    let validationPass = true;
    setNameError('');
    setFromDateError('');
    setToDateError('');
    if (!eventName.trim()) {
      setNameError('Event requires a name');
      validationPass = false;
    }
    if (!fromDate) {
      setFromDateError('From date is required');
      validationPass = false;
    }
    if (!toDate) {
      setToDateError('To date is required');
      validationPass = false;
    }
    if (!selectedTeamId) {
      validationPass = false;
    }
    return validationPass;
  }

  const save = async () => {
    if (!validate() || !fromDate || !toDate || !selectedTeamId || error) {
      return;
    }
    setIsLoading(true);
    try {
      await createTeamEventRequest({
        eventName: eventName,
        fromDate: fromDate,
        toDate: toDate,
        teamId: selectedTeamId,
      });
      dispatch(successNotification('Team event added'));
      dispatch(loadTeamEvents({teamId: selectedTeamId}));
      dispatch(closeDialog());
    } catch (err: any) {
      dispatch(failureNotification(err?.message ?? 'Failed to add team event'));
    } finally {
      setIsLoading(false);
    }
  }

  const close = () => {
    dispatch(closeDialog());
  }

  return (
    <>
      <Dialog isOpen={true} onClose={close} showLogo={true}>
        {isLoading && <LoadingSpinner fullScreen={true} />}
        <DialogHeader title="work-planner.add-event-project" />

        {error && <BodyError style={{marginBottom: 24}}>{error}</BodyError>}

        <TextField onChange={setEventName}
                   label="work-planner.create-event-title"
                   value={eventName}
                   error={nameError}
                   dataTest={'team-event-name-field'}
                   fullWidth={true} />

        <DateInputRow>
          <HalfWidthDateInputField onClick={() => setIsCalendarOpen(true)}
                                   onChange={() => {}}
                                   placeholder="textinput.add-date"
                                   value={fromDate ? fromDate.format('D MMMM YYYY') : ''}
                                   label="textinput.from"
                                   error={fromDateError}
                                   editableOnly={true}/>

          <HalfWidthDateInputField onClick={() => setIsCalendarOpen(true)}
                                   onChange={() => {}}
                                   placeholder="textinput.add-date"
                                   value={toDate ? toDate.format('D MMMM YYYY') : ''}
                                   label="textinput.to"
                                   error={toDateError}
                                   editableOnly={true}/>
        </DateInputRow>

        <div style={{marginBottom: 32}}>
          <TeamDropDownList label="work-planner.event-is-for"
                            teams={teams}
                            onSelect={setSelectedTeamId}
                            selectedTeamId={selectedTeamId ?? ''}/>
        </div>

        <PrimaryButton click={save}
                       text="work-planner.add-event-project"
                       fullWidth={true}
                       size={ButtonSize.Large}
                       dataTest={'save-team-event-button'}
                       disabled={isLoading} />
      </Dialog>
      <FullScreenDateRangeSelector open={isCalendarOpen} onDateSelect={onDateSelected}
                                   startDate={fromDate} endDate={toDate}
                                   onClose={() => setIsCalendarOpen(false)}/>
    </>
  )
}

const DateInputRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (${smallTablet}) {
    flex-direction: row;
  }
`

const HalfWidthDateInputField = styled(DateInputField)`
  width: 100%;
  @media (${smallTablet}) {
    width: 47.5%;
  }
`
